import React from 'react'
import { motion } from 'framer-motion'
import Reveal from '@/components/motion/Reveal'
import SplitText from '@/components/motion/SplitText'
import EditableText from '@/components/editable/EditableText'
import { useSiteEdit } from '@/contexts/SiteEditContext'

const VERBS = [
  { n: '01', t: 'Transformer', d: 'Provoquer une bascule, un avant et un après — une expérience qui marque.' },
  { n: '02', t: 'Accueillir', d: 'Une atmosphère cohérente où chacun trouve sa place et son énergie.' },
  { n: '03', t: 'Connecter', d: 'Tisser un réseau humain entre artistes, jeunes, entrepreneurs et institutions.' },
  { n: '04', t: 'Transmettre', d: "Ateliers, formations, immersions, séjours, laboratoires d'idées." },
  { n: '05', t: 'Créer', d: 'Studio médias, IA, contenus pédagogiques, plateformes en ligne.' },
  { n: '06', t: 'Structurer', d: 'Documenter, protocoliser, former, automatiser — pour grandir durablement.' },
]

const Vision: React.FC = () => {
  const { get, canEdit, editMode } = useSiteEdit()
  return (
    <section id="vision" className="relative bg-[#0E1B2C] py-32 lg:py-44 overflow-hidden">
      {/* Background ambience */}
      <motion.div
        className="absolute -left-32 top-1/3 w-[500px] h-[500px] rounded-full blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(184,194,107,0.14), transparent 70%)' }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.6, 0.9, 0.6] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Section header */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-20">
          <Reveal className="lg:col-span-4">
            <div className="text-[10px] tracking-luxe text-[#C8A24A] mb-4">
              <EditableText as="span" contentKey="vision.section.kicker" section="Vision" label="Numéro de section">01 — VISION</EditableText>
            </div>
            <motion.div
              className="h-px bg-gradient-to-r from-[#C8A24A] to-transparent mb-6"
              initial={{ width: 0 }}
              whileInView={{ width: 96 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            />
            <EditableText
              as="div"
              contentKey="vision.eyebrow"
              section="Vision"
              label="Vision — sous-titre"
              className="text-[12px] tracking-mid uppercase text-[#C9BFA8]"
            >
              Un véritable écosystème vivant
            </EditableText>
          </Reveal>
          <div className="lg:col-span-8">
            <h3 className="font-serif-display text-[36px] sm:text-[48px] lg:text-[64px] leading-[1.05] text-[#E8DCC4] text-balance">
              {canEdit && editMode ? (
                <EditableText contentKey="vision.title.line1" section="Vision" label="Titre — ligne 1" as="span">
                  {get('vision.title.line1', "Le Centre n'est pas qu'un lieu —")}
                </EditableText>
              ) : (
                <SplitText text={get('vision.title.line1', "Le Centre n'est pas qu'un lieu —")} stagger={0.05} />
              )}
              <br />
              {canEdit && editMode ? (
                <EditableText contentKey="vision.title.line2" section="Vision" label="Titre — ligne 2" as="span" className="italic text-[#C9BFA8]">
                  {get('vision.title.line2', "c'est une expérience, une signature, un univers.")}
                </EditableText>
              ) : (
                <SplitText
                  text={get('vision.title.line2', "c'est une expérience, une signature, un univers.")}
                  delay={0.4}
                  stagger={0.05}
                  className="italic text-[#C9BFA8]"
                />
              )}
            </h3>
            <Reveal delay={0.6}>
              <EditableText
                as="p"
                multiline
                contentKey="vision.paragraph1"
                section="Vision"
                label="Vision — paragraphe 1"
                className="mt-8 max-w-3xl text-[17px] leading-[1.7] text-[#C9BFA8]"
              >
                Notre objectif : construire une structure capable d'évoluer, une identité forte, une expérience marquante, un fonctionnement reproductible. Un modèle qui grandit sans dépendre d'une seule personne — un écosystème qui transforme, accueille, connecte, transmet et fait émerger des projets.
              </EditableText>
            </Reveal>
            <Reveal delay={0.75}>
              <EditableText
                as="p"
                multiline
                contentKey="vision.paragraph2"
                section="Vision"
                label="Vision — paragraphe 2"
                className="mt-6 max-w-3xl text-[16px] leading-[1.75] text-[#C9BFA8]/90"
              >
                Le Centre rassemble des compétences humaines, créatives, pédagogiques et technologiques dans une structure pensée pour évoluer durablement. Chaque dispositif développé peut être transmis, reproduit et adapté à différents contextes : institutions, structures sociales, formations, événements ou projets indépendants.
              </EditableText>
            </Reveal>
          </div>
        </div>

        {/* Six verbs grid with stagger */}
        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[rgba(232,220,196,0.10)] hairline"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={{
            visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
          }}
        >
          {VERBS.map((b) => (
            <motion.div
              key={b.n}
              variants={{
                hidden: { opacity: 0, y: 30, filter: 'blur(8px)' },
                visible: {
                  opacity: 1,
                  y: 0,
                  filter: 'blur(0px)',
                  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
                },
              }}
              whileHover={{ backgroundColor: '#11171A' }}
              transition={{ duration: 0.4 }}
              className="relative bg-[#0E1B2C] p-8 lg:p-10 group cursor-default overflow-hidden"
            >
              {/* Hover gradient */}
              <motion.div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  background: 'radial-gradient(400px circle at 50% 0%, rgba(201,210,122,0.08), transparent 70%)',
                }}
              />

              <div className="relative flex items-start justify-between mb-6">
                <div className="text-[10px] tracking-luxe text-[#C8A24A]">{b.n}</div>
                <motion.div
                  className="h-px bg-[#C8A24A]"
                  initial={{ width: 40 }}
                  whileHover={{ width: 80 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>

              <motion.div
                className="relative font-serif-display text-[32px] text-[#E8DCC4] mb-4"
                whileHover={{ x: 4 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              >
                {b.t}
              </motion.div>
              <p className="relative text-[14px] leading-[1.7] text-[#C9BFA8]">{b.d}</p>

              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9E26A]/40 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left" />
            </motion.div>
          ))}
        </motion.div>

        {/* Closing pull-quote */}
        <motion.div
          className="mt-24 max-w-3xl mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            className="h-px bg-gradient-to-r from-transparent via-[#C8A24A] to-transparent mx-auto mb-10"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          />
          <p className="font-serif-display italic text-[24px] sm:text-[30px] lg:text-[36px] leading-[1.25] text-[#E8DCC4] text-balance">
            <EditableText contentKey="vision.closing" section="Vision" label="Citation de clôture" multiline as="span">
              Créer un modèle vivant capable de continuer à transmettre au-delà des individus.
            </EditableText>
          </p>
          <motion.div
            className="h-px bg-gradient-to-r from-transparent via-[#C8A24A] to-transparent mx-auto mt-10"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          />
        </motion.div>
      </div>
    </section>
  )
}

export default Vision
